import type { OnlineOverpaymentDto } from '@bqcrm/shared';
import { Alert, Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { IconReceiptRefund } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { can, useMe } from '../auth';
import { formatDateTime } from '../format';
import { notifyError, notifyOk } from '../notify';
import { Money } from './Money';

/**
 * docs/24: nadpłaty z płatności online (np. klient zapłacił dwa razy) — CRM nie zwraca pieniędzy sam. Księgowość robi
 * zwrot w panelu ING Pay i oznacza go tutaj („Zwrócono”), co zamyka zadanie „Zwrot nadpłaty z płatności online”.
 */
export function OnlineOverpayments({ caseId }: { caseId: string }) {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const key = ['online-overpayments', caseId];
  const q = useQuery({ queryKey: key, queryFn: () => api<OnlineOverpaymentDto[]>(`/cases/${caseId}/online-overpayments`) });
  const [open, setOpen] = useState<OnlineOverpaymentDto | null>(null);
  const [note, setNote] = useState('');
  const resolve = useMutation({
    mutationFn: (id: string) => api<OnlineOverpaymentDto[]>(`/online-overpayments/${id}/resolve`, { method: 'POST', body: { note } }),
    onSuccess: (rows) => {
      qc.setQueryData(key, rows);
      void qc.invalidateQueries({ queryKey: ['case-tasks'] });
      setOpen(null);
      setNote('');
      notifyOk('Zwrot oznaczony — zadanie zamknięte');
    },
    onError: notifyError,
  });
  const rows = q.data ?? [];
  if (!rows.length) return null;
  const canResolve = can(me, 'finanse.wplaty');
  return (
    <>
      <Stack gap="xs">
        {rows.map((o) => (
          <Alert key={o.id} color={o.resolvedAt ? 'gray' : 'orange'} icon={<IconReceiptRefund />} title={o.resolvedAt ? 'Nadpłata online zwrócona' : 'Nadpłata z płatności online — do zwrotu'}>
            <Group justify="space-between" wrap="wrap" gap="xs">
              <div>
                <Text size="sm">
                  <Money gr={o.amountGr} />
                  {o.invoiceNumber ? ` · ${o.invoiceNumber}` : ''} · transakcja ING Pay {o.transactionId} · {formatDateTime(o.createdAt)}
                </Text>
                {o.resolvedAt ? (
                  <Text size="xs" c="dimmed">
                    zwrot oznaczony {formatDateTime(o.resolvedAt)}
                    {o.resolvedByName ? ` przez ${o.resolvedByName}` : ''}
                    {o.note ? ` · ${o.note}` : ''}
                  </Text>
                ) : (
                  <Text size="xs" c="dimmed">
                    Zwróć tę kwotę klientowi w panelu ING Pay (Transakcje → zwrot), potem oznacz zwrot tutaj.
                  </Text>
                )}
              </div>
              {!o.resolvedAt && canResolve && (
                <Button size="xs" variant="default" onClick={() => setOpen(o)}>
                  Zwrócono
                </Button>
              )}
            </Group>
          </Alert>
        ))}
      </Stack>
      <Modal opened={!!open} onClose={() => setOpen(null)} title="Zwrot nadpłaty online">
        {open && (
          <Stack>
            <Text size="sm">
              Potwierdź, że zwróciłeś klientowi <Money gr={open.amountGr} /> w panelu ING Pay.
            </Text>
            <Textarea label="Notatka (opcjonalnie)" placeholder="np. zwrot z dnia, numer zwrotu w ING Pay" value={note} onChange={(e) => setNote(e.currentTarget.value)} maxLength={500} autosize minRows={2} />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setOpen(null)}>
                Anuluj
              </Button>
              <Button onClick={() => resolve.mutate(open.id)} loading={resolve.isPending}>
                Oznacz jako zwrócone
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
